import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import Accordion from 'relements/build/esm/ui/accordion';

import { redirectToNode } from '@src/containers/Redirector';
import { BASE_URL, GOOGLE_ANALYTICS } from '@src/const';
import { sendGoogleAnalyticsEvents } from '@src/actions/_utils';
import InfoTooltip from '@src/components/InfoTooltip/InfoTooltip';
import Icon from '@src/components/Icon';

import { NODE_TYPE_MAPPING, ENTITY_TYPES } from '../../constants/index';
import styles from './DebugInfo.scss';

/**
 * This function renders the info from raw logs in a prettier
 * and easy to understand way
 * @param {Object} prop - Object containing all the props
 * @param {Object} prop.debugData - contains the raw logs of the respective message
 */
function DebugInfo({ debugData }) {
  // sets active state of detected entities accordion, by default open
  const [isActive, setActive] = useState(true);

  // redirects to a particular node
  const openNode = useCallback((nodeId, destination = 'nodes') => {
    redirectToNode(nodeId, destination);

    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.DEBUG,
      GOOGLE_ANALYTICS.EVENTS.OPEN_DEBUG_LINK,
      GOOGLE_ANALYTICS.LABEL.DEBUG_OPEN_NODE,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
  }, []);

  // renders basic details of debug logs (like bot info, business info etc.)
  const renderBasicDetails = useCallback(() => {
    return (
      <>
        <div className={styles.debugInfoHeading}>Basic Details</div>
        <div className={styles.debugInfoData}>
          <div className={styles.debugInfoDataHeading}>Business Name</div>
          <div className={`${styles.debugInfoDataAnswer}`}>
            {debugData.business_via_name}
          </div>
        </div>
        <div className={styles.debugInfoData}>
          <div className={styles.debugInfoDataHeading}>Bot Name</div>
          <div className={`${styles.debugInfoDataAnswer}`}>
            {debugData.domain.predicted_domain}
          </div>
        </div>
        <div className={styles.debugInfoData}>
          <div className={styles.debugInfoDataHeading}>
            Bot Detection Method
          </div>
          <div className={styles.debugInfoDataAnswer}>
            {debugData.domain.method}
          </div>
        </div>
        <div className={styles.debugInfoData}>
          <div className={styles.debugInfoDataHeading}>Bot Language</div>
          <div className={styles.debugInfoDataAnswer}>
            {debugData.detected_language}
          </div>
        </div>
      </>
    );
  }, [debugData]);

  const renderBotSynonymsLogs = useCallback(() => {
    if (!debugData.bot_synonym_replacements?.length) return null;

    return (
      <>
        <div className={styles.debugInfoHeading}>Bot Synonym Replacements</div>
        {debugData.bot_synonym_replacements.map((botSynonymLog, index) => (
          <>
            {index ? (
              <div
                className={`${styles.debugInfoSeperator} ${styles.debugInfoSeperatorSmall}`}
              />
            ) : null}
            <div className={styles.debugInfoData}>
              <div className={styles.debugInfoDataHeading}>Original</div>
              <div className={`${styles.debugInfoDataAnswer}`}>
                {botSynonymLog.original}
              </div>
            </div>
            <div className={styles.debugInfoData}>
              <div className={styles.debugInfoDataHeading}>Replacement</div>
              <div className={`${styles.debugInfoDataAnswer}`}>
                {botSynonymLog.replacement}
              </div>
            </div>
          </>
        ))}
      </>
    );
  }, [debugData]);

  // renders when no node is detected
  const renderZeroState = useCallback(() => {
    return (
      <div className={styles.debugInfoNoNode}>
        <InfoTooltip isError>Looks like no step was detected</InfoTooltip>
      </div>
    );
  }, []);

  // renders fallback entity (entity types which are not added in the list on frontend)
  const renderFallbackEntity = useCallback((entity) => {
    if (typeof entity.entity_value === 'string') {
      return (
        <div className={styles.debugInfoDataNoPadding}>
          <div
            className={`${styles.debugInfoDataHeading} ${styles.debugInfoDataColor}`}
          >
            Value
          </div>
          <div
            className={`${styles.debugInfoDataAnswer} ${styles.debugInfoDataColor}`}
          >
            {entity.entity_value}
          </div>
        </div>
      );
    }

    if (
      typeof entity.entity_value === 'object' &&
      typeof entity.entity_value.value === 'string'
    ) {
      return (
        <div className={styles.debugInfoDataNoPadding}>
          <div
            className={`${styles.debugInfoDataHeading} ${styles.debugInfoDataColor}`}
          >
            Value
          </div>
          <div
            className={`${styles.debugInfoDataAnswer} ${styles.debugInfoDataColor}`}
          >
            {entity.entity_value.value}
          </div>
        </div>
      );
    }

    return (
      <InfoTooltip hideIcon>
        To view more information about this entity click on the &lsquo;Detailed
        info&rsquo; tab on the Debug logs.
      </InfoTooltip>
    );
  }, []);

  // renders all the static nodes detected
  const renderStaticNodes = useCallback(() => {
    const totalNodes = debugData.node_list.length - 1;
    return (
      <>
        {debugData.node_list.map((staticNode, i) => (
          <>
            <div className={styles.debugInfoData}>
              <div className={styles.debugInfoDataHeading}>Step Detected</div>
              <div
                className={`${styles.debugInfoDataAnswer} ${styles.debugInfoDataLink}`}
                onClick={() => openNode(staticNode.node_id)}
              >
                {staticNode.athena_name}
              </div>
            </div>
            <div className={styles.debugInfoData}>
              <div className={styles.debugInfoDataHeading}>Step Type</div>
              <div className={styles.debugInfoDataAnswer}>
                {NODE_TYPE_MAPPING[staticNode.node_type]}
              </div>
            </div>
            <div className={styles.debugInfoData}>
              <div className={styles.debugInfoDataHeading}>
                Step Detection Method
              </div>
              <div className={styles.debugInfoDataAnswer}>
                {staticNode.node_detection_method}
              </div>
            </div>
            {// we check if it is the last node of the array, don't add seperator at the end
            i !== totalNodes ? (
              <div
                className={`${styles.debugInfoSeperator} ${styles.debugInfoSeperatorSmall}`}
              />
            ) : null}
          </>
        ))}
      </>
    );
  }, [debugData, openNode]);

  // renders all the detected nodes (calls renderStaticNodes function for static nodes)
  const renderNodes = useCallback(() => {
    return (
      <>
        {renderStaticNodes()}
        {// TODO: later when multiple code nodes can be executed, remove hardcoded array object
        debugData.detected_code_nodes && (
          <>
            <div
              className={`${styles.debugInfoSeperator} ${styles.debugInfoSeperatorSmall}`}
            />
            <div className={styles.debugInfoData}>
              <div className={styles.debugInfoDataHeading}>Step Detected</div>
              <div
                className={`${styles.debugInfoDataAnswer} ${styles.debugInfoDataLink}`}
                onClick={() =>
                  // TODO: later when multiple code nodes can be executed, remove hardcoded array object
                  openNode(debugData.detected_code_nodes[0].id, 'code-nodes')
                }
              >
                {debugData.detected_code_nodes[0].athena_name}
              </div>
            </div>
            <div className={styles.debugInfoData}>
              <div className={styles.debugInfoDataHeading}>Step Type</div>
              <div className={styles.debugInfoDataAnswer}>
                {NODE_TYPE_MAPPING[3]}
              </div>
            </div>
            <div className={styles.debugInfoData}>
              <div className={styles.debugInfoDataHeading}>Code log link</div>
              <div
                className={`${styles.debugInfoDataAnswer} ${styles.debugInfoDataLink}`}
              >
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.logLink}
                  href={`${BASE_URL}code_executor/logs/${debugData.detected_code_nodes[0].function_name}/`}
                >
                  View logs
                </a>
              </div>
            </div>
          </>
        )}
        {debugData.detected_output_node && (
          <>
            <div
              className={`${styles.debugInfoSeperator} ${styles.debugInfoSeperatorSmall}`}
            />
            <div className={styles.debugInfoData}>
              <div className={styles.debugInfoDataHeading}>Step Detected</div>
              <div
                className={`${styles.debugInfoDataAnswer} ${styles.debugInfoDataLink}`}
                onClick={() =>
                  openNode(debugData.detected_output_node.id, 'output-nodes')
                }
              >
                {debugData.detected_output_node.athena_name}
              </div>
            </div>
            <div className={styles.debugInfoData}>
              <div className={styles.debugInfoDataHeading}>Step Type</div>
              <div className={styles.debugInfoDataAnswer}>
                {NODE_TYPE_MAPPING[4]}
              </div>
            </div>
          </>
        )}
      </>
    );
  }, [debugData, openNode, renderStaticNodes]);

  /**
   * Render the Header of detected entity accordion
   * @param {string} entityCount Total number of detected entities
   */
  const renderEntityHeader = useCallback(
    (entityCount) => {
      const arrowClass = isActive
        ? `${styles.detectedEntitiesIcon} ${styles.detectedEntitiesIconActive}`
        : styles.detectedEntitiesIcon;

      return (
        <div className={styles.detectedEntities}>
          <div className={styles.detectedEntitiesHeading}>
            Detected Entities
          </div>
          <div className={styles.detectedEntitiesNumber}>
            <div className={styles.detectedEntitiesNumberTotal}>
              Total: {entityCount}
            </div>
            <Icon iconType="down" className={arrowClass} />
          </div>
        </div>
      );
    },
    [isActive]
  );

  // renders all the detected entity items
  const renderEntityItems = useCallback(() => {
    return Object.keys(debugData.entities).map((item) => {
      if (
        Array.isArray(debugData.entities[item]) &&
        debugData.entities[item].length
      ) {
        const entity = debugData.entities[item][0];

        switch (entity.entity_type) {
          case ENTITY_TYPES.BUDGET:
            return (
              <div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div className={styles.debugInfoDataHeading}>Entity Name</div>
                  <div className={styles.debugInfoDataAnswer}>{item}</div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div className={styles.debugInfoDataHeading}>Type</div>
                  <div className={styles.debugInfoDataAnswer}>
                    {entity.entity_type}
                  </div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div
                    className={`${styles.debugInfoDataHeading} ${styles.debugInfoDataColor}`}
                  >
                    Minimum Value
                  </div>
                  <div
                    className={`${styles.debugInfoDataAnswer} ${styles.debugInfoDataColor}`}
                  >
                    {entity.entity_value.min_budget}
                  </div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div
                    className={`${styles.debugInfoDataHeading} ${styles.debugInfoDataColor}`}
                  >
                    Maximum Value
                  </div>
                  <div
                    className={`${styles.debugInfoDataAnswer} ${styles.debugInfoDataColor}`}
                  >
                    {entity.entity_value.max_budget}
                  </div>
                </div>
              </div>
            );

          case ENTITY_TYPES.REGEX:
            return (
              <div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div className={styles.debugInfoDataHeading}>Entity Name</div>
                  <div className={styles.debugInfoDataAnswer}>{item}</div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div className={styles.debugInfoDataHeading}>Type</div>
                  <div className={styles.debugInfoDataAnswer}>
                    {entity.entity_type}
                  </div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div
                    className={`${styles.debugInfoDataHeading} ${styles.debugInfoDataColor}`}
                  >
                    Regex
                  </div>
                  <div
                    className={`${styles.debugInfoDataAnswer} ${styles.debugInfoDataColor}`}
                  >
                    {entity.regex}
                  </div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div
                    className={`${styles.debugInfoDataHeading} ${styles.debugInfoDataColor}`}
                  >
                    Value
                  </div>
                  <div
                    className={`${styles.debugInfoDataAnswer} ${styles.debugInfoDataColor}`}
                  >
                    {entity.entity_value.value}
                  </div>
                </div>
              </div>
            );

          case ENTITY_TYPES.PHONE_NUMBER:
          case ENTITY_TYPES.NUMBER:
            return (
              <div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div className={styles.debugInfoDataHeading}>Entity Name</div>
                  <div className={styles.debugInfoDataAnswer}>{item}</div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div className={styles.debugInfoDataHeading}>Type</div>
                  <div className={styles.debugInfoDataAnswer}>
                    {entity.entity_type}
                  </div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div
                    className={`${styles.debugInfoDataHeading} ${styles.debugInfoDataColor}`}
                  >
                    Value
                  </div>
                  <div
                    className={`${styles.debugInfoDataAnswer} ${styles.debugInfoDataColor}`}
                  >
                    {entity.entity_value}
                  </div>
                </div>
              </div>
            );

          case ENTITY_TYPES.TIME:
            return (
              <div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div className={styles.debugInfoDataHeading}>Entity Name</div>
                  <div className={styles.debugInfoDataAnswer}>{item}</div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div className={styles.debugInfoDataHeading}>Type</div>
                  <div className={styles.debugInfoDataAnswer}>
                    {entity.entity_type}
                  </div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div
                    className={`${styles.debugInfoDataHeading} ${styles.debugInfoDataColor}`}
                  >
                    Value
                  </div>
                  <div
                    className={`${styles.debugInfoDataAnswer} ${styles.debugInfoDataColor}`}
                  >
                    {entity.entity_value.hh.toString().length === 2
                      ? entity.entity_value.hh
                      : `0${entity.entity_value.hh}`}
                    :
                    {entity.entity_value.mm.toString().length === 2
                      ? entity.entity_value.mm
                      : `0${entity.entity_value.mm}`}{' '}
                    {entity.entity_value.nn}
                  </div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div
                    className={`${styles.debugInfoDataHeading} ${styles.debugInfoDataColor}`}
                  >
                    Timezone
                  </div>
                  <div
                    className={`${styles.debugInfoDataAnswer} ${styles.debugInfoDataColor}`}
                  >
                    {entity.entity_value.tz}
                  </div>
                </div>
              </div>
            );

          case ENTITY_TYPES.DATE:
            return (
              <div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div className={styles.debugInfoDataHeading}>Entity Name</div>
                  <div className={styles.debugInfoDataAnswer}>{item}</div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div className={styles.debugInfoDataHeading}>Type</div>
                  <div className={styles.debugInfoDataAnswer}>
                    {entity.entity_type}
                  </div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div
                    className={`${styles.debugInfoDataHeading} ${styles.debugInfoDataColor}`}
                  >
                    Value
                  </div>
                  <div
                    className={`${styles.debugInfoDataAnswer} ${styles.debugInfoDataColor}`}
                  >
                    {entity.entity_value.dd}/{entity.entity_value.mm}/
                    {entity.entity_value.yy}
                  </div>
                </div>
              </div>
            );

          case ENTITY_TYPES.ATTACHMENT:
            return (
              <div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div className={styles.debugInfoDataHeading}>Entity Name</div>
                  <div className={styles.debugInfoDataAnswer}>{item}</div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div className={styles.debugInfoDataHeading}>Type</div>
                  <div className={styles.debugInfoDataAnswer}>
                    {entity.entity_type}
                  </div>
                </div>
                <InfoTooltip hideIcon>
                  To view all the attachment links click on the &lsquo;Detailed
                  info&rsquo; tab on the Debug logs.
                </InfoTooltip>
              </div>
            );

          case ENTITY_TYPES.TEXT:
            return (
              <div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div className={styles.debugInfoDataHeading}>Entity Name</div>
                  <div className={styles.debugInfoDataAnswer}>{item}</div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div className={styles.debugInfoDataHeading}>Type</div>
                  <div className={styles.debugInfoDataAnswer}>
                    {entity.entity_type}
                  </div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div
                    className={`${styles.debugInfoDataHeading} ${styles.debugInfoDataColor}`}
                  >
                    Value
                  </div>
                  <div
                    className={`${styles.debugInfoDataAnswer} ${styles.debugInfoDataColor}`}
                  >
                    {entity.entity_value}
                  </div>
                </div>
              </div>
            );

          case ENTITY_TYPES.CURRENCY:
            return (
              <div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div className={styles.debugInfoDataHeading}>Entity Name</div>
                  <div className={styles.debugInfoDataAnswer}>{item}</div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div className={styles.debugInfoDataHeading}>Type</div>
                  <div className={styles.debugInfoDataAnswer}>
                    {entity.entity_type}
                  </div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div
                    className={`${styles.debugInfoDataHeading} ${styles.debugInfoDataColor}`}
                  >
                    Unit
                  </div>
                  <div
                    className={`${styles.debugInfoDataAnswer} ${styles.debugInfoDataColor}`}
                  >
                    {entity.unit}
                  </div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div
                    className={`${styles.debugInfoDataHeading} ${styles.debugInfoDataColor}`}
                  >
                    Value
                  </div>
                  <div
                    className={`${styles.debugInfoDataAnswer} ${styles.debugInfoDataColor}`}
                  >
                    {entity.entity_value.abs_value}
                  </div>
                </div>
              </div>
            );

          case ENTITY_TYPES.PERSON_NAME:
            return (
              <div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div className={styles.debugInfoDataHeading}>Entity Name</div>
                  <div className={styles.debugInfoDataAnswer}>{item}</div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div className={styles.debugInfoDataHeading}>Type</div>
                  <div className={styles.debugInfoDataAnswer}>
                    {entity.entity_type}
                  </div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div
                    className={`${styles.debugInfoDataHeading} ${styles.debugInfoDataColor}`}
                  >
                    First Name
                  </div>
                  <div
                    className={`${styles.debugInfoDataAnswer} ${styles.debugInfoDataColor}`}
                  >
                    {entity.entity_value.first_name}
                  </div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div
                    className={`${styles.debugInfoDataHeading} ${styles.debugInfoDataColor}`}
                  >
                    Middle Name
                  </div>
                  <div
                    className={`${styles.debugInfoDataAnswer} ${styles.debugInfoDataColor}`}
                  >
                    {entity.entity_value.middle_name}
                  </div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div
                    className={`${styles.debugInfoDataHeading} ${styles.debugInfoDataColor}`}
                  >
                    Last Name
                  </div>
                  <div
                    className={`${styles.debugInfoDataAnswer} ${styles.debugInfoDataColor}`}
                  >
                    {entity.entity_value.last_name}
                  </div>
                </div>
              </div>
            );

          default:
            return (
              <div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div className={styles.debugInfoDataHeading}>Entity Name</div>
                  <div className={styles.debugInfoDataAnswer}>{item}</div>
                </div>
                <div className={styles.debugInfoDataNoPadding}>
                  <div className={styles.debugInfoDataHeading}>Type</div>
                  <div className={styles.debugInfoDataAnswer}>
                    {entity.entity_type}
                  </div>
                </div>
                {// fallback option to display entity value because ml/backend unsure about entities
                renderFallbackEntity(entity)}
              </div>
            );
        }
      }
    });
  }, [debugData, renderFallbackEntity]);

  // render the entities, uses renderEntityHeader and renderEntityItems
  const renderEntities = useCallback(() => {
    // if no entity object is present, return null
    if (typeof debugData.entities === 'undefined') return null;

    // holds the total count of detected entity
    let entityCount = 0;

    // iterates over entities object, and sets the count to detected entity
    // we do this, because even when an entity is not detected, its key might show up in the object
    // if an entity is not detected, it comes as null
    Object.keys(debugData.entities).forEach((item) => {
      if (
        Array.isArray(debugData.entities[item]) &&
        debugData.entities[item].length
      )
        entityCount += 1;
    });

    // total entity is 0, return
    if (!entityCount) return null;

    return (
      <Accordion>
        <Accordion.Item
          className={styles.detectedEntitiesNoBorder}
          active={isActive}
          onChange={() => setActive(!isActive)}
          renderHeader={renderEntityHeader(entityCount)}
        >
          <div className={styles.detectedEntitiesBody}>
            {renderEntityItems()}
          </div>
        </Accordion.Item>
      </Accordion>
    );
  }, [debugData, isActive, renderEntityHeader, renderEntityItems]);

  // renders original and translated message
  const renderTranslationLogs = useCallback(
    () => (
      <>
        <div className={styles.debugInfoHeading}>Translation Details</div>
        <div className={styles.debugInfoData}>
          <div className={styles.debugInfoDataHeading}>Original Message</div>
          <div className={styles.debugInfoDataAnswer}>
            {debugData.original_message}
          </div>
        </div>
        <div className={styles.debugInfoData}>
          <div className={styles.debugInfoDataHeading}>Translated Message</div>
          <div className={styles.debugInfoDataAnswer}>
            {debugData.translated_message}
          </div>
        </div>
        <div className={styles.debugInfoSeperator} />
      </>
    ),
    [debugData.original_message, debugData.translated_message]
  );

  return (
    <div className={styles.debugInfoWrapper}>
      {renderBasicDetails()}
      <div className={styles.debugInfoSeperator} />
      <div className={styles.debugInfoHeading}>Step Details</div>
      {debugData.node_list.length ? renderNodes() : renderZeroState()}
      <div className={styles.debugInfoSeperator} />
      {debugData.translated && renderTranslationLogs()}
      {renderEntities()}
      {renderBotSynonymsLogs()}
    </div>
  );
}

DebugInfo.propTypes = {
  debugData: PropTypes.shape({
    business_via_name: PropTypes.string,
    domain: PropTypes.shape({
      predicted_domain: PropTypes.string,
      method: PropTypes.string,
    }),
    node_list: PropTypes.arrayOf(
      PropTypes.shape({
        node: PropTypes.string,
        athena_name: PropTypes.string,
      })
    ),
    detected_code_nodes: PropTypes.arrayOf(
      PropTypes.shape({
        node_name: PropTypes.string,
        athena_name: PropTypes.string,
        function_name: PropTypes.string,
      })
    ),
    detected_output_node: PropTypes.arrayOf(
      PropTypes.shape({
        node_name: PropTypes.string,
        athena_name: PropTypes.string,
      })
    ),
    entities: PropTypes.shape({}),
    user_message_source_language: PropTypes.string,
    original_message: PropTypes.string,
    translated_message: PropTypes.string,
    detected_language: PropTypes.string,
    translated: PropTypes.bool,
    bot_synonym_replacements: PropTypes.arrayOf(
      PropTypes.shape({
        original: PropTypes.string,
        replacement: PropTypes.string,
      })
    ),
  }),
};

DebugInfo.defaultProps = {
  debugData: {
    domain: {
      business_via_name: '',
      predicted_domain: '',
      method: '',
    },
    detected_code_nodes: undefined,
    detected_output_node: undefined,
    node_list: [],
    user_message_source_language: '',
    original_message: '',
    translated_message: '',
    detected_language: '',
    translated: false,
    bot_synonym_replacements: [],
  },
};

export default DebugInfo;
